import React, { useEffect, useState } from "react";
import { MessageCircle, TrendingUp } from "lucide-react";
import { RATES as FALLBACK_RATES, SMALL_RATES as FALLBACK_SMALL, BULLION_GROUP } from "../mock";
import RateChart from "./RateChart";
import api from "../lib/api";

const SectionHeader = ({ eyebrow, title, sub }) => (
  <div className="text-center mb-14">
    <div className="flex items-center justify-center gap-3 mb-4">
      <span className="section-line" />
      <span className="font-ui text-[11px] tracking-[0.3em] uppercase text-gold flex items-center gap-2">
        <span className="h-1.5 w-1.5 rounded-full bg-gold" />{eyebrow}
      </span>
      <span className="section-line rotate-180" />
    </div>
    <h2 className="font-display smallcaps text-4xl md:text-6xl text-white">{title}</h2>
    {sub && <p className="font-body italic text-gray-400 mt-4 text-lg">{sub}</p>}
  </div>
);

export { SectionHeader };

const Rates = () => {
  const [RATES, setRates] = useState(FALLBACK_RATES);
  const [history, setHistory] = useState([]);
  const SMALL_RATES = RATES.small_rates || FALLBACK_SMALL;

  useEffect(() => {
    api.get("/rates").then((r) => setRates(r.data)).catch(() => {});
    api.get("/rates/history?days=14").then((r) => setHistory(r.data)).catch(() => {});
  }, []);

  const bullionHref = BULLION_GROUP.inviteLink
    ? BULLION_GROUP.inviteLink
    : `https://wa.me/${BULLION_GROUP.number}?text=${encodeURIComponent(BULLION_GROUP.subscribeMessage)}`;

  return (
    <section id="rates" className="relative py-24 bg-[#0b0f19]">
      <div className="max-w-[1200px] mx-auto px-6 md:px-8">
        <SectionHeader eyebrow="Today's Store Rates" title="Gold & Silver Price Today" sub={`Store rates for ${RATES.city} · Updated daily by TwinBrain`} />

        <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
          {/* Gold card */}
          <div className="relative overflow-hidden border border-gold/25 bg-gradient-to-br from-gold/[0.08] to-transparent p-8 card-glow">
            <div className="absolute top-0 left-0 h-[2px] w-full bg-gradient-to-r from-gold to-transparent" />
            <span className="font-ui text-[10px] tracking-[0.25em] uppercase text-gold">24K Pure Gold</span>
            <h3 className="font-display text-2xl text-white mt-2">Gold · {RATES.gold24k.fineness}</h3>
            <div className="font-display gold-gradient-text text-5xl mt-4">₹{RATES.gold24k.value.toLocaleString('en-IN')}</div>
            <p className="font-body text-gray-400 mt-1">{RATES.gold24k.unit}</p>
            <span className="inline-block mt-5 bg-gold/15 text-gold font-ui text-[10px] tracking-wider uppercase px-3 py-1.5">Store Rate</span>
            <p className="font-ui text-[10px] text-muted-foreground mt-4 tracking-wide">Updated: {RATES.updated}</p>
          </div>

          {/* Silver card */}
          <div className="relative overflow-hidden border border-white/12 bg-white/[0.02] p-8 card-glow">
            <div className="absolute top-0 left-0 h-[2px] w-full bg-gradient-to-r from-gray-300/60 to-transparent" />
            <span className="font-ui text-[10px] tracking-[0.25em] uppercase text-gray-400">Pure Silver</span>
            <h3 className="font-display text-2xl text-white mt-2">Silver · {RATES.silver.fineness}</h3>
            <div className="font-display text-5xl mt-4 text-gray-100">₹{RATES.silver.value.toLocaleString('en-IN')}</div>
            <p className="font-body text-gray-400 mt-1">{RATES.silver.unit}</p>
            <span className="inline-block mt-5 bg-white/10 text-gray-200 font-ui text-[10px] tracking-wider uppercase px-3 py-1.5">Store Rate</span>
            <p className="font-ui text-[10px] text-muted-foreground mt-4 tracking-wide">Updated: {RATES.updated}</p>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-4 max-w-3xl mx-auto mt-6">
          {SMALL_RATES.map((r, i) => (
            <div key={i} className="border border-white/8 bg-white/[0.02] p-5 text-center card-glow">
              <div className="font-display text-2xl text-gold">{r.label}</div>
              <div className="font-ui text-[9px] tracking-[0.2em] uppercase text-muted-foreground mt-1">{r.name}</div>
              <div className="font-display text-xl text-white mt-2">{r.price}</div>
            </div>
          ))}
        </div>
        <p className="font-body italic text-center text-gray-500 text-sm mt-8">* Contact us for exact buying rates. Prices may vary slightly.</p>

        {/* Price trend chart */}
        <div className="max-w-4xl mx-auto mt-14 border border-white/10 bg-white/[0.02] p-6 md:p-8">
          <div className="flex items-center gap-2 mb-5">
            <TrendingUp className="text-gold" size={20} />
            <h3 className="font-display text-2xl text-white">Recent Price Movement</h3>
          </div>
          <RateChart data={history} height={240} />
        </div>

        {/* Paid bullion WhatsApp group CTA */}
        <div className="max-w-4xl mx-auto mt-6 relative overflow-hidden border border-gold/30 bg-gradient-to-br from-gold/[0.1] to-transparent p-8 md:p-10">
          <div className="flex flex-col md:flex-row md:items-center gap-6">
            <div className="h-16 w-16 rounded-full bg-gold/15 border border-gold/40 flex items-center justify-center text-gold shrink-0">
              <MessageCircle size={30} />
            </div>
            <div className="flex-1">
              <span className="font-ui text-[10px] tracking-[0.25em] uppercase text-gold">Premium · {BULLION_GROUP.price}</span>
              <h3 className="font-display text-3xl text-white mt-1">Live Bullion Rates WhatsApp Group</h3>
              <p className="font-body text-gray-400 mt-2 max-w-xl">Get real-time gold &amp; silver bullion rates on WhatsApp throughout the day. Paid members-only group — message us to subscribe.</p>
            </div>
            <a href={bullionHref} target="_blank" rel="noreferrer"
              className="gold-btn font-ui text-xs tracking-[0.2em] uppercase px-8 py-4 font-medium whitespace-nowrap text-center transition-all hover:shadow-lg hover:shadow-gold/20">
              Join Paid Group →
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Rates;
